// frontend/src/pages/profile/MyDevicesCard.tsx
//
// Spec D7 — the person who owns the phones spots a stolen login faster than any
// admin will, so a rep sees their own two slots and can end either.
//
// Both slots are always drawn, full or empty. An empty slot is as much
// information as a full one: it is where a second device would have to go, and
// that is exactly what is refused.
//
// 🔒 A FAILED LOAD MUST NOT DRAW AN EMPTY CARD. Two blank slots is an
// affirmative "nothing is signed in to your account" — the single most
// reassuring thing this card can say, and a lie when the request simply failed.
// On failure the card says so and offers a retry, INSTEAD of the slots.
//
// 🔒 NO SIGN-OUT BUTTON ON THE SESSION YOU ARE READING THIS IN. Pressing it
// ends your own session; the reload that follows 401s, the axios interceptor
// throws you at /login, and nothing on screen ever explained why. Ending your
// own session is what "Sign out everywhere" one card below is for — deliberate,
// named, and behind a confirmation.
//
// 🔒 PLATFORM ONLY (P3). No product vocabulary — copied to Sun Stellar unchanged.

import { useEffect, useState } from 'react';
import { Loader2, Smartphone, Monitor } from 'lucide-react';
import { api } from '../../utils/api';
import { agoWords } from '../../utils/agoWords';

interface Slot {
  channel: 'web' | 'apk';
  sessionId: string | null;
  label: string | null;
  clientVersion: string | null;
  networkName: string | null;
  lastSeenAt: string | null;
}

export function MyDevicesCard() {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [currentSessionId, setCurrent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [endingId, setEndingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get('/sessions/me');
      setSlots(res.data.data.slots ?? []);
      setCurrent(res.data.data.currentSessionId ?? null);
      setFailed(false);
    } catch {
      // 🔒 Drop the stale rows on the way out. Leaving them on screen under a
      // failure notice would be two answers to one question.
      setSlots([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function endSlot(sessionId: string) {
    setEndingId(sessionId);
    try {
      await api.delete(`/sessions/me/${sessionId}`);
      await load();
    } catch {
      setFailed(true);
    } finally {
      setEndingId(null);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <h3 className="mb-1 text-lg font-semibold text-slate-900">My devices</h3>
      <p className="mb-4 text-sm text-slate-600">
        You get one phone and one browser at a time. If you do not recognise something here, sign it
        out and change your password.
      </p>

      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
      ) : failed ? (
        <div className="rounded-lg border p-3" style={{ borderColor: '#d77b76', background: '#fff5f4' }}>
          <p className="text-sm font-medium" style={{ color: '#9b3936' }}>
            Could not load your devices — try again.
          </p>
          <p className="mt-1 text-xs text-slate-600">
            This is not a sign that nothing is signed in. We simply could not ask.
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {slots.map(slot => {
            const isPhone = slot.channel === 'apk';
            const word = isPhone ? 'phone' : 'browser';
            const Icon = isPhone ? Smartphone : Monitor;
            const isThis = slot.sessionId != null && slot.sessionId === currentSessionId;
            const when = slot.lastSeenAt ? agoWords(slot.lastSeenAt) : null;
            return (
              <div key={slot.channel} className="rounded-lg border border-slate-200 p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {isPhone ? 'Phone' : 'Browser'}
                </p>
                {slot.sessionId ? (
                  <>
                    <div className="mt-2 flex items-start gap-2">
                      <Icon className="mt-0.5 h-4 w-4 text-slate-500" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">{slot.label}</p>
                        {/* 🔒 The time is the point. A label alone ("Redmi Note 12")
                            says nothing — "last used 4 minutes ago" while the handset
                            is in your pocket is how a rep spots a login that is not
                            theirs, which is the whole reason for this card (D7). */}
                        <p className="text-xs text-slate-600">
                          {[
                            isThis ? `This ${word}` : slot.clientVersion,
                            when ? (isThis ? `signed in ${when}` : when) : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                        {slot.networkName && <p className="text-xs text-slate-500">{slot.networkName}</p>}
                      </div>
                    </div>
                    {isThis ? (
                      <p className="mt-3 text-xs text-slate-400">
                        You are using this {word} now. To end it, use “Sign out everywhere” below.
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void endSlot(slot.sessionId as string)}
                        disabled={endingId === slot.sessionId}
                        className="mt-3 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                        style={{ borderColor: '#d77b76', color: 'var(--danger, #c34a48)' }}
                      >
                        {endingId === slot.sessionId && <Loader2 className="h-3 w-3 animate-spin" />}
                        Sign out the {word}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="mt-3 text-sm text-slate-400">Not signed in</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
